/**
 * Swiss Open House — White & Clay
 *
 * Archetype: Swiss/International. Everything hangs off a single left axis, the
 * page is mostly air, and one clay-red mark carries all the emphasis. The
 * headline is set in a heavy grotesque at a size that would be vulgar in any
 * other layout and reads as confidence in this one.
 */

import { CANVAS, SPACE, TYPE, RULE } from '../../system/tokens';
import { getPalette, scrim } from '../../system/palettes';
import { roles } from '../../system/typography';
import { grid } from '../../system/grid';
import { text, shape, rule, image, page } from '../../system/elements';
import { statRow } from '../../system/blocks';
import { PHOTOS } from '../../system/photos';

const ID = 'tpl-flyer-real-estate-03';

export default {
  id: ID,
  meta: {
    name: 'Swiss Open House — White & Clay',
    collection: 'signature',
    archetype: 'swiss-left-axis',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'swiss-clay',
    pairing: 'archivo-inter',
    tags: ['real estate', 'flyer', 'open house', 'swiss', 'modern', 'minimal', 'bold', 'us letter'],
    description: 'Swiss-grid open-house flyer: heavy grotesque headline on a single left axis, generous white space, one clay-red accent.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('swiss-clay');
    const r = roles('archivo-inter');
    const g = grid(canvas);
    const els = [];

    // ── Masthead rule + running heads ───────────────────────────────────────
    let y = g.margin;
    els.push(rule(`${ID}-top-rule`, { x: g.left, y, width: g.contentWidth, weight: RULE.thin, color: p.ink }));
    y += SPACE.xs;

    els.push(text(`${ID}-kicker-left`, r.eyebrow, 'Open House', { x: g.left, y, width: g.span(6) }, { color: p.accent }));
    els.push(text(`${ID}-kicker-right`, r.eyebrow, 'Saturday 12–3 pm', { x: g.x(6), y, width: g.span(6), align: 'right' }, { color: p.ink }));
    y += TYPE.eyebrow * 1.4 + SPACE.xl;

    // ── Headline ────────────────────────────────────────────────────────────
    const headline = text(`${ID}-headline`, r.display, '24 Kestrel\nStreet', {
      x: g.left, y, width: g.contentWidth,
    }, { color: p.ink, fontSize: 250 });
    els.push(headline);
    y += headline.height + SPACE.sm;

    const sub = text(`${ID}-sub`, r.h3, 'Portland, Oregon 97210', { x: g.left, y, width: g.contentWidth }, { color: p.inkMuted });
    els.push(sub);
    y += sub.height + SPACE.lg;

    // Clay mark — the one saturated element on the page.
    els.push(shape(`${ID}-mark`, { x: g.left, y, width: 300, height: RULE.thick * 2, fill: p.accent }));
    y += RULE.thick * 2 + SPACE.lg;

    // ── Photograph ──────────────────────────────────────────────────────────
    const PHOTO_H = 950;
    els.push(image(`${ID}-hero`, { x: g.left, y, width: g.contentWidth, height: PHOTO_H, src: PHOTOS.modernWhiteVilla }));
    y += PHOTO_H + SPACE.xs;

    els.push(text(`${ID}-caption`, r.caption, 'West elevation, pool terrace', {
      x: g.left, y, width: g.contentWidth, align: 'right',
    }, { color: p.inkMuted }));
    y += TYPE.caption * 1.4 + SPACE.lg;

    // ── Specification row ───────────────────────────────────────────────────
    els.push(rule(`${ID}-spec-rule`, { x: g.left, y, width: g.contentWidth, weight: RULE.hair, color: scrim(p.ink, 0.3) }));
    y += SPACE.sm;

    const stats = statRow(ID, r, {
      x: g.left, y, width: g.contentWidth,
      stats: [
        { value: '4', label: 'Beds' },
        { value: '3', label: 'Baths' },
        { value: '2,960', label: 'Sq Ft' },
        { value: '$985K', label: 'Price' },
      ],
      valueColor: p.ink, labelColor: p.inkMuted, dividerColor: scrim(p.ink, 0.2),
    });
    els.push(...stats.elements);

    // ── Footer, pinned ──────────────────────────────────────────────────────
    const footY = canvas.height - g.margin - 150;
    els.push(rule(`${ID}-foot-rule`, { x: g.left, y: footY, width: g.contentWidth, weight: RULE.thin, color: p.ink }));
    els.push(text(`${ID}-agent`, r.cta, 'Marielle Vance · Harbour & Vine', {
      x: g.left, y: footY + SPACE.xs, width: g.span(7),
    }, { color: p.ink }));
    els.push(text(`${ID}-contact`, r.cta, '(503) 555-0164', {
      x: g.x(7), y: footY + SPACE.xs, width: g.span(5), align: 'right',
    }, { color: p.accent }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
